<template>
  <div id="dashboard-analytics" class="dashboard-wrapper">
    <!-- Header -->
    <div class="dashboard-header">
      <div class="d-flex flex-wrap align-items-center justify-content-between">
        <div class="header-content">
          <h1 class="dashboard-title">Performance Dashboard</h1>
          <p class="dashboard-subtitle">Overview of agents, leads, calls and subscription revenue</p>
        </div>
        <div class="filter-controls">
          <div class="date-group">
            <label>From</label>
            <b-form-input type="date" size="sm" v-model="filters.startDate" />
          </div>
          <div class="date-group">
            <label>To</label>
            <b-form-input type="date" size="sm" v-model="filters.endDate" />
          </div>
          <b-button variant="primary" size="sm" class="btn-apply" @click="applyFilters">
            <feather-icon icon="CalendarIcon" size="14" class="mr-50" />
            Apply
          </b-button>
          <b-button variant="light" size="sm" @click="clearFilters">Clear</b-button>
        </div>
      </div>
    </div>

    <!-- KPI cards with gradients -->
    <b-row class="kpi-row">
      <b-col md="6" lg="4" xl class="mb-3">
        <div class="kpi-card kpi-primary">
          <div class="kpi-icon-wrap">
            <feather-icon icon="UsersIcon" class="kpi-icon" />
          </div>
          <div class="kpi-content">
            <p class="kpi-label">Total Agents</p>
            <h2 class="kpi-value">{{ metrics.totalAgents | formatNumber }}</h2>
            <span class="kpi-desc">Registered agents</span>
          </div>
        </div>
      </b-col>
      <b-col md="6" lg="4" xl class="mb-3">
        <div class="kpi-card kpi-info">
          <div class="kpi-icon-wrap">
            <feather-icon icon="TargetIcon" class="kpi-icon" />
          </div>
          <div class="kpi-content">
            <p class="kpi-label">Total Leads</p>
            <h2 class="kpi-value">{{ metrics.totalLeads | formatNumber }}</h2>
            <span class="kpi-desc">Generated in period</span>
          </div>
        </div>
      </b-col>
      <b-col md="6" lg="4" xl class="mb-3">
        <div class="kpi-card kpi-warning">
          <div class="kpi-icon-wrap">
            <feather-icon icon="PhoneCallIcon" class="kpi-icon" />
          </div>
          <div class="kpi-content">
            <p class="kpi-label">Total Calls</p>
            <h2 class="kpi-value">{{ metrics.totalCalls | formatNumber }}</h2>
            <span class="kpi-desc">AI calls logged</span>
          </div>
        </div>
      </b-col>
      <b-col md="6" lg="4" xl class="mb-3">
        <div class="kpi-card kpi-success">
          <div class="kpi-icon-wrap">
            <feather-icon icon="ClockIcon" class="kpi-icon" />
          </div>
          <div class="kpi-content">
            <p class="kpi-label">Call Duration</p>
            <h2 class="kpi-value">{{ metrics.totalCallMinutes | formatNumber }}<small> min</small></h2>
            <span class="kpi-desc">Total talk time</span>
          </div>
        </div>
      </b-col>
      <b-col md="6" lg="4" xl class="mb-3">
        <div class="kpi-card kpi-danger">
          <div class="kpi-icon-wrap">
            <feather-icon icon="DollarSignIcon" class="kpi-icon" />
          </div>
          <div class="kpi-content">
            <p class="kpi-label">Revenue</p>
            <h2 class="kpi-value">${{ metrics.totalRevenue | formatNumber }}</h2>
            <span class="kpi-desc">Subscription revenue</span>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Charts -->
    <b-row>
      <b-col lg="6" class="mb-4">
        <div class="chart-card">
          <div class="chart-card-header">
            <h5 class="chart-title">Lead Status Breakdown</h5>
            <feather-icon icon="BarChart2Icon" class="chart-icon" />
          </div>
          <p class="chart-subtitle">Distribution of leads by status</p>
          <div class="chart-body">
            <apexchart type="donut" height="280" :options="leadsStatusOptions" :series="displayLeadsSeries" />
          </div>
        </div>
      </b-col>
      <b-col lg="6" class="mb-4">
        <div class="chart-card">
          <div class="chart-card-header">
            <h5 class="chart-title">Calls Over Time</h5>
            <feather-icon icon="ActivityIcon" class="chart-icon" />
          </div>
          <p class="chart-subtitle">Daily AI call volume</p>
          <div class="chart-body">
            <apexchart type="bar" height="280" :options="callsBarOptions" :series="displayCallsSeries" />
          </div>
        </div>
      </b-col>
      <b-col lg="6" class="mb-4">
        <div class="chart-card">
          <div class="chart-card-header">
            <h5 class="chart-title">Revenue Trend</h5>
            <feather-icon icon="DollarSignIcon" class="chart-icon" />
          </div>
          <p class="chart-subtitle">Monthly subscription revenue</p>
          <div class="chart-body">
            <apexchart type="area" height="280" :options="revenueOptions" :series="revenueSeries" />
          </div>
        </div>
      </b-col>
      <b-col lg="6" class="mb-4">
        <div class="chart-card">
          <div class="chart-card-header">
            <h5 class="chart-title">Call Duration by Day</h5>
            <feather-icon icon="ClockIcon" class="chart-icon" />
          </div>
          <p class="chart-subtitle">Total minutes of calls per day</p>
          <div class="chart-body">
            <apexchart type="line" height="280" :options="durationOptions" :series="durationSeries" />
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Loading overlay -->
    <div v-if="loading" class="dashboard-loading">
      <b-spinner variant="primary" />
      <p class="mt-2 mb-0">Loading dashboard...</p>
    </div>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import { mapActions } from "vuex";

export default {
  name: "SignalsureDashboard",
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    return {
      loading: false,
      filters: {
        startDate: null,
        endDate: null,
      },
      metrics: {
        totalAgents: 0,
        totalLeads: 0,
        totalCalls: 0,
        totalCallMinutes: 0,
        totalRevenue: 0,
      },
      leadsStatusSeries: [],
      leadsStatusLabels: [],
      leadsStatusOptions: {
        chart: { type: "donut" },
        labels: [],
        legend: { position: "bottom" },
        dataLabels: { enabled: true },
        colors: ["#e3e7fe", "#b8c5f8", "#7c8ef5", "#5e6fd4", "#ff9f43", "#28c76f"],
      },
      callsBarSeries: [],
      callsBarOptions: {
        chart: { type: "bar", toolbar: { show: false } },
        xaxis: { categories: [], labels: { rotate: -45 } },
        plotOptions: { bar: { columnWidth: "55%", borderRadius: 4 } },
        dataLabels: { enabled: false },
        colors: ["#b8c5f8"],
      },
      revenueSeries: [{ name: "Revenue", data: [] }],
      revenueOptions: {
        chart: { type: "area", toolbar: { show: false }, sparkline: { enabled: false } },
        xaxis: { categories: [] },
        colors: ["#9e95f5"],
        fill: { type: "gradient", gradient: { shadeIntensity: 0.5, opacityFrom: 0.5, opacityTo: 0.1 } },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
      },
      durationSeries: [{ name: "Minutes", data: [] }],
      durationOptions: {
        chart: { type: "line", toolbar: { show: false } },
        xaxis: { categories: [] },
        colors: ["#48da89"],
        stroke: { curve: "smooth", width: 2 },
        dataLabels: { enabled: false },
        markers: { size: 4 },
      },
    };
  },
  computed: {
    displayLeadsSeries() {
      if (this.leadsStatusSeries.length) return this.leadsStatusSeries;
      return [45, 38, 42, 31];
    },
    displayCallsSeries() {
      if (this.callsBarSeries.length) return this.callsBarSeries;
      return [{ name: "Calls", data: [12, 19, 8, 15, 22, 14, 9] }];
    },
  },
  filters: {
    formatNumber(value) {
      if (value === null || value === undefined) return "0";
      return Number(value).toLocaleString();
    },
  },
  mounted() {
    this.fetchDashboard();
  },
  methods: {
    ...mapActions("appData", [
      "getAdminDashboardStats",
    ]),

    buildBackendDateParams() {
      const params = {};
      if (this.filters.startDate) {
        params.start_date = this.filters.startDate;
      }
      if (this.filters.endDate) {
        params.end_date = this.filters.endDate;
      }
      return params;
    },

    applyFilters() {
      this.fetchDashboard();
    },

    clearFilters() {
      this.filters.startDate = null;
      this.filters.endDate = null;
      this.fetchDashboard();
    },

    async fetchDashboard() {
      this.loading = true;
      try {
        const dateParams = this.buildBackendDateParams();
        const res = await this.getAdminDashboardStats(dateParams);

        if (res.status !== 200 || !res.data) {
          throw new Error("Invalid response from admin dashboard API");
        }

        const data = res.data;

        // KPIs
        this.metrics.totalAgents = data.total_agents || 0;
        this.metrics.totalLeads = data.total_leads || 0;
        this.metrics.totalCalls = data.total_calls || 0;
        this.metrics.totalCallMinutes = data.total_call_minutes || 0;
        this.metrics.totalRevenue = data.total_revenue || 0;

        // Charts
        this.prepareLeadsChart(data.lead_status_breakdown);
        this.prepareCallsChart(data.calls_over_time);
        this.prepareRevenueChart(data.revenue_trends);
        this.prepareDurationChart(data.call_duration_by_day);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to load dashboard data", e);
      } finally {
        this.loading = false;
      }
    },

    prepareLeadsChart(leadStatusData) {
      if (!leadStatusData || typeof leadStatusData !== "object") {
        this.leadsStatusSeries = [];
        this.leadsStatusLabels = [];
        this.leadsStatusOptions = { ...this.leadsStatusOptions, labels: [] };
        return;
      }
      const labels = Object.keys(leadStatusData);
      const series = labels.map((l) => leadStatusData[l]);
      this.leadsStatusSeries = series;
      this.leadsStatusLabels = labels;
      this.leadsStatusOptions = { ...this.leadsStatusOptions, labels };
    },

    prepareCallsChart(callsOverTime) {
      if (!callsOverTime || typeof callsOverTime !== "object") {
        this.callsBarSeries = [];
        this.callsBarOptions = { ...this.callsBarOptions, xaxis: { ...this.callsBarOptions.xaxis, categories: [] } };
        return;
      }
      const dates = Object.keys(callsOverTime).sort();
      const data = dates.map((d) => callsOverTime[d]);

      this.callsBarSeries = [{ name: "Calls", data }];
      this.callsBarOptions = {
        ...this.callsBarOptions,
        xaxis: { ...this.callsBarOptions.xaxis, categories: dates },
      };
    },

    prepareRevenueChart(revenueTrends) {
      if (!revenueTrends || typeof revenueTrends !== "object") {
        this.revenueSeries = [{ name: "Revenue", data: [] }];
        this.revenueOptions = {
          ...this.revenueOptions,
          xaxis: { ...this.revenueOptions.xaxis, categories: [] },
        };
        return;
      }
      const labels = Object.keys(revenueTrends);
      const data = labels.map((l) => revenueTrends[l]);

      this.revenueSeries = [{ name: "Revenue", data }];
      this.revenueOptions = {
        ...this.revenueOptions,
        xaxis: { ...this.revenueOptions.xaxis, categories: labels },
      };
    },

    prepareDurationChart(durationByDay) {
      if (!durationByDay || typeof durationByDay !== "object") {
        this.durationSeries = [{ name: "Minutes", data: [] }];
        this.durationOptions = {
          ...this.durationOptions,
          xaxis: { ...this.durationOptions.xaxis, categories: [] },
        };
        return;
      }
      const labels = Object.keys(durationByDay);
      const data = labels.map((l) => durationByDay[l]);

      this.durationSeries = [{ name: "Minutes", data }];
      this.durationOptions = {
        ...this.durationOptions,
        xaxis: { ...this.durationOptions.xaxis, categories: labels },
      };
    },
  },
};
</script>

<style scoped>
.dashboard-wrapper {
  min-height: 100%;
  position: relative;
}

/* Header */
.dashboard-header {
  background: #f8f7ff;
  border: 1px solid #e8e6f9;
  border-radius: 12px;
  padding: 1.5rem 1.75rem;
  margin-bottom: 1.5rem;
  color: #5e5873;
}

.dashboard-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
  letter-spacing: -0.02em;
  color: #5e5873;
}

.dashboard-subtitle {
  margin: 0;
  color: #6e6b7b;
  font-size: 0.875rem;
}

.filter-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.75rem;
}

.date-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.date-group label {
  font-size: 0.75rem;
  color: #6e6b7b;
  margin: 0;
}

.date-group input {
  border-radius: 6px;
  border: none;
  background: white;
  color: #5e5873;
  min-width: 140px;
  padding: 0.35rem 0.6rem;
}

.btn-apply {
  background: #7367f0 !important;
  color: white !important;
  border: none;
}

/* KPI Cards */
.kpi-row {
  margin-bottom: 0.5rem;
}

.kpi-card {
  border-radius: 12px;
  padding: 1.25rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  height: 100%;
  min-height: 120px;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.kpi-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.kpi-icon {
  width: 24px;
  height: 24px;
}

.kpi-primary {
  background: #f0eff9;
  border-left: 4px solid #7367f0;
  color: #5e5873;
}

.kpi-primary .kpi-icon-wrap {
  background: #e3e0fc;
}

.kpi-primary .kpi-icon {
  color: #7367f0;
}

.kpi-info {
  background: #e8f8fb;
  border-left: 4px solid #00cfe8;
  color: #5e5873;
}

.kpi-info .kpi-icon-wrap {
  background: #c5f1f7;
}

.kpi-info .kpi-icon {
  color: #00cfe8;
}

.kpi-warning {
  background: #fff5eb;
  border-left: 4px solid #ff9f43;
  color: #5e5873;
}

.kpi-warning .kpi-icon-wrap {
  background: #ffe4cc;
}

.kpi-warning .kpi-icon {
  color: #ff9f43;
}

.kpi-success {
  background: #e8faf0;
  border-left: 4px solid #28c76f;
  color: #5e5873;
}

.kpi-success .kpi-icon-wrap {
  background: #c5f0d5;
}

.kpi-success .kpi-icon {
  color: #28c76f;
}

.kpi-danger {
  background: #fef2f2;
  border-left: 4px solid #ea5455;
  color: #5e5873;
}

.kpi-danger .kpi-icon-wrap {
  background: #fbd5d5;
}

.kpi-danger .kpi-icon {
  color: #ea5455;
}

.kpi-content {
  flex: 1;
  min-width: 0;
}

.kpi-label {
  font-size: 0.8125rem;
  margin: 0 0 0.25rem 0;
  color: #6e6b7b;
  font-weight: 500;
}

.kpi-value {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: #5e5873;
}

.kpi-value small {
  font-size: 0.9rem;
  font-weight: 500;
  color: #6e6b7b;
}

.kpi-desc {
  font-size: 0.75rem;
  color: #6e6b7b;
}

/* Chart cards */
.chart-card {
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
  height: 100%;
  transition: box-shadow 0.2s;
}

.chart-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.chart-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
}

.chart-title {
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
  color: #5e5873;
}

.chart-icon {
  color: #b9b9c3;
  width: 20px;
  height: 20px;
}

.chart-subtitle {
  font-size: 0.8125rem;
  color: #b9b9c3;
  margin: 0 0 1rem 0;
}

.chart-body {
  margin-top: 0.5rem;
}

.chart-empty {
  padding: 3rem 1rem;
  text-align: center;
  color: #b9b9c3;
}

/* Loading overlay */
.dashboard-loading {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

@media (max-width: 768px) {
  .dashboard-header {
    padding: 1rem;
  }

  .filter-controls {
    width: 100%;
    margin-top: 1rem;
  }

  .kpi-value {
    font-size: 1.5rem;
  }
}
</style>

